import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon } from './AdminShell';
import DataTable from './DataTable';
import { Modal, Summary, Checklist } from './Overlay';
import { text } from './peopleCopy';

export const payoutGql = (query, variables) => fetch('/graphql', {
  method: 'post', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }), credentials: 'include'
}).then(r => r.json());
export const PAYOUTS_QUERY = `query ($currentPage:Int,$limit:Int,$searchList:String,$payoutStatus:String) {
  getAdminPayouts(currentPage:$currentPage,limit:$limit,searchList:$searchList,payoutStatus:$payoutStatus) {
    count pendingAmount pendingCount paidMonthAmount paidMonthCount failedAmount failedCount eligibleCount status errorMessage
    results { id provider amount currency jobs method status date reason transactionId eligible }
  }
}`;
export const PAYOUT_MUTATION = `mutation ($id:Int!,$retry:Boolean) { manualPayout(id:$id,retry:$retry) { status errorMessage } }`;
const fmt = n => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
export const payoutMoney = (n, currency = 'AED') => `${currency} ${fmt(n)}`;
export const payoutDay = value => {
  const d = new Date(Number(value) || value);
  return isNaN(d) ? '—' : d.toISOString().slice(0, 10);
};
const STATUS = {
  paid: ['Paid', 'ok'], pending: ['Pending', 'warn'],
  failed: ['Failed', 'danger'], held: ['Held', 'info']
};
const Tag = ({ value, locale }) => {
  const [label, kind] = STATUS[value] || [value, ''];
  const ar = { Paid: 'مدفوعة', Pending: 'معلقة', Failed: 'فشلت', Held: 'محتجزة' };
  return <span className={`tag ${kind}`}><i />{locale === 'ar' ? (ar[label] || label) : label}</span>;
};
const Tile = ({ label, value, note, color }) => <div className="tile" style={{ '--accent': color }}>
  <div className="k">{label}</div><div className="row"><div className="v num">AED {fmt(value)}</div></div>
  <div className="note">{note}</div>
</div>;

class Payouts extends React.Component {
  state = { rows: [], count: 0, page: 1, perPage: 10, search: '', payoutStatus: '',
    pendingAmount: 0, pendingCount: 0, paidMonthAmount: 0, paidMonthCount: 0,
    failedAmount: 0, failedCount: 0, eligibleCount: 0, loading: true, error: null,
    releasing: null, bulk: null, viewing: null, saving: false, notice: null };
  locale = () => this.props.intl && this.props.intl.locale;
  tx = (en, ar) => text(this.locale(), en, ar);
  componentDidMount() { this.load(); }
  vars = extra => ({ currentPage: this.state.page, limit: this.state.perPage,
    searchList: this.state.search || undefined, payoutStatus: this.state.payoutStatus || undefined, ...extra });
  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await payoutGql(PAYOUTS_QUERY, this.vars()).catch(() => null);
    const res = json && json.data && json.data.getAdminPayouts;
    if (!res || res.status !== 200) {
      this.setState({ loading: false, error: (res && res.errorMessage) || 'Could not load payouts.' });
      return;
    }
    this.setState({ rows: res.results || [], count: res.count || 0,
      pendingAmount: res.pendingAmount || 0, pendingCount: res.pendingCount || 0,
      paidMonthAmount: res.paidMonthAmount || 0, paidMonthCount: res.paidMonthCount || 0,
      failedAmount: res.failedAmount || 0, failedCount: res.failedCount || 0, loading: false });
    this.setState({ eligibleCount: res.eligibleCount || 0 });
  };
  openBulk = async () => {
    const json = await payoutGql(PAYOUTS_QUERY, this.vars({ currentPage: 1, limit: 1000, payoutStatus: 'pending' })).catch(() => null);
    const res = json && json.data && json.data.getAdminPayouts;
    const rows = res && res.status === 200 ? (res.results || []).filter(r => r.eligible) : [];
    if (!rows.length) {
      this.setState({ notice: { tone: 'amber', text: this.tx('No eligible payouts are ready to release.', 'لا توجد دفعات مؤهلة جاهزة للصرف.') } });
      return;
    }
    this.setState({ bulk: rows, notice: null });
  };
  releaseBulk = async () => {
    const rows = this.state.bulk;
    this.setState({ saving: true });
    let completed = 0;
    let failure = null;
    for (const row of rows) {
      const json = await payoutGql(PAYOUT_MUTATION, { id: row.id }).catch(() => null);
      const res = json && json.data && json.data.manualPayout;
      if (!res || res.status !== 200) { failure = (res && res.errorMessage) || this.tx('Transfer failed.', 'فشل التحويل.'); break; }
      completed += 1;
    }
    this.setState({ saving: false, bulk: null, notice: failure
      ? { tone: 'danger', text: `${completed} released, then stopped: ${failure}` }
      : { tone: 'amber', text: this.tx(`${completed} payout${completed === 1 ? '' : 's'} released.`, `تم صرف ${completed} دفعة.`) } });
    this.load(true);
  };
  release = async () => {
    const row = this.state.releasing;
    this.setState({ saving: true });
    const json = await payoutGql(PAYOUT_MUTATION, { id: row.id, retry: row.status === 'failed' }).catch(() => null);
    const res = json && json.data && json.data.manualPayout;
    this.setState({ saving: false });
    if (!res || res.status !== 200) {
      this.setState({ notice: { tone: 'danger', text: (res && res.errorMessage) || this.tx('Transfer failed.', 'فشل التحويل.') } });
      return;
    }
    this.setState({ releasing: null, notice: { tone: 'amber', text: this.tx(`${payoutMoney(row.amount, row.currency)} released to ${row.provider}.`, `تم صرف ${payoutMoney(row.amount, row.currency)} إلى ${row.provider}.`) } });
    this.load(true);
  };
  columns = () => [
    { key: 'id', label: this.tx('Payout', 'الدفعة'), render: r => <span className="mono">PO-{r.id}</span> },
    { key: 'provider', label: this.tx('Provider', 'مقدم الخدمة'), sortable: true },
    { key: 'jobs', label: this.tx('Jobs', 'الطلبات'), align: 'end', sortable: true },
    { key: 'amount', label: this.tx('Amount', 'المبلغ'), align: 'end', sortable: true,
      render: r => <span className="num">{payoutMoney(r.amount, r.currency)}</span> },
    { key: 'method', label: this.tx('Method', 'الطريقة') },
    { key: 'status', label: this.tx('Status', 'الحالة'), render: r => <Tag value={r.status} locale={this.locale()} /> },
    { key: 'date', label: this.tx('Date', 'التاريخ'), align: 'end', render: r => <span className="mono">{payoutDay(r.date)}</span> },
    { key: 'act', label: '', align: 'end', render: r => r.status === 'paid'
      ? <button className="btn icon" type="button" title={this.tx('View transfer', 'عرض التحويل')}
          aria-label={`${this.tx('View payout', 'عرض الدفعة')} PO-${r.id}`} onClick={() => this.setState({ viewing: r })}><Icon name="eye" /></button>
      : r.status === 'failed'
        ? <button className="btn ghost sm" type="button" disabled={!r.eligible}
            title={r.eligible ? this.tx('Retry transfer', 'إعادة محاولة التحويل') : this.tx('Provider has no verified payout method', 'لا يملك مقدم الخدمة طريقة دفع موثقة')}
            onClick={() => this.setState({ releasing: r, notice: null })}>{this.tx('Retry', 'إعادة المحاولة')}</button>
        : r.status === 'pending'
          ? r.eligible
            ? <button className="btn sm" type="button"
                onClick={() => this.setState({ releasing: r, notice: null })}>{this.tx('Release', 'صرف')}</button>
            : <span className="muted">{this.tx('Method required', 'الطريقة مطلوبة')}</span>
          : <span className="muted">{this.tx('On hold', 'محتجزة')}</span> }
  ];
  render() {
    const s = this.state;
    return <AdminShell current="payouts" breadcrumb={[this.tx('Money', 'المال'), this.tx('Payouts', 'الدفعات')]} title={this.tx('Payouts', 'الدفعات')}
      lead={this.tx('Provider earnings and settlement status.', 'أرباح مقدمي الخدمة وحالة التسوية.')}
      actions={[<button className="btn" type="button" key="release" disabled={!s.eligibleCount}
        onClick={this.openBulk} title={this.tx('Release every eligible pending payout', 'صرف كل الدفعات المعلقة المؤهلة')}><Icon name="wallet" />{this.tx('Release pending', 'صرف المعلّق')}</button>,
      <a className="btn ghost" href="/siteadmin/payout" key="classic">{this.tx('Open in classic', 'فتح النسخة الكلاسيكية')}</a>]}> 
      {s.notice && <div className={`notice ${s.notice.tone}`}><span className="ni">{s.notice.tone === 'danger' ? '!' : '✓'}</span>
        <span className="nt">{s.notice.text}</span></div>}
      {!!s.pendingCount && !s.eligibleCount && <div className="notice info"><span className="ni"><Icon name="wallet" /></span>
        <span className="nt"><b>{this.tx('No pending payout is ready to release.', 'لا توجد دفعة معلقة جاهزة للصرف.')}</b> {this.tx('Providers need to configure a default payout method first.', 'يجب على مقدمي الخدمة إعداد طريقة دفع افتراضية أولاً.')}</span></div>}
      <div className="grid g-3" style={{ marginBottom: 'var(--sp-4)' }}>
        <Tile label={this.tx('Pending', 'معلقة')} value={s.pendingAmount} color="var(--warn)" note={this.tx(`${s.pendingCount} jobs pending · ${s.eligibleCount} ready`, `${s.pendingCount} طلبات معلقة · ${s.eligibleCount} جاهزة`)} />
        <Tile label={this.tx('Paid this month', 'مدفوعة هذا الشهر')} value={s.paidMonthAmount} color="var(--ok)" note={this.tx(`${s.paidMonthCount} settled jobs`, `${s.paidMonthCount} طلبات تمت تسويتها`)} />
        <Tile label={this.tx('Failed', 'فشلت')} value={s.failedAmount} color="var(--danger)" note={this.tx(`${s.failedCount} transfers need attention`, `${s.failedCount} تحويلات تحتاج إلى مراجعة`)} />
      </div>
      {s.releasing && <Modal icon="wallet" tone={s.releasing.status === 'failed' ? 'danger' : ''}
        title={`${s.releasing.status === 'failed' ? this.tx('Retry', 'إعادة المحاولة') : this.tx('Release', 'صرف')} ${payoutMoney(s.releasing.amount, s.releasing.currency)}?`}
        sub={this.tx(`Transfers to ${s.releasing.provider} via ${s.releasing.method}.`, `تحويل إلى ${s.releasing.provider} عبر ${s.releasing.method}.`)}
        confirmLabel={s.releasing.status === 'failed' ? this.tx('Retry transfer', 'إعادة محاولة التحويل') : this.tx('Release funds', 'صرف الأموال')}
        busy={s.saving} onConfirm={this.release} onClose={() => this.setState({ releasing: null })}>
        <Summary pairs={[
          [this.tx('Payout', 'الدفعة'), `PO-${s.releasing.id}`], [this.tx('Provider', 'مقدم الخدمة'), s.releasing.provider],
          [this.tx('Jobs covered', 'الطلبات المشمولة'), '1'], [this.tx('Amount', 'المبلغ'), payoutMoney(s.releasing.amount, s.releasing.currency)],
          [this.tx('Method', 'الطريقة'), s.releasing.method], ...(s.releasing.reason ? [[this.tx('Last failure', 'آخر فشل'), s.releasing.reason]] : [])
        ]} />
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--tx-2)' }}>
          {this.tx('Money moves as soon as you confirm. Stripe transfers cannot be cancelled.', 'تتحرك الأموال فور التأكيد. لا يمكن إلغاء تحويلات Stripe.')}
        </p>
      </Modal>}
      {s.bulk && <Modal icon="wallet" title={`${this.tx('Release', 'صرف')} ${s.bulk.length} ${this.tx('payouts?', 'دفعات؟')}`}
        sub={this.tx('Transfers run one at a time and stop on the first failure.', 'تُنفذ التحويلات واحداً تلو الآخر وتتوقف عند أول فشل.')}
        confirmLabel={this.tx('Release pending', 'صرف المعلّق')} busy={s.saving} onConfirm={this.releaseBulk}
        onClose={() => this.setState({ bulk: null })}>
        <Checklist items={s.bulk.map(r => ({ label: r.provider,
          detail: `${payoutMoney(r.amount, r.currency)} · PO-${r.id}` }))} />
      </Modal>}
      {s.viewing && <Modal icon="wallet" title={`${this.tx('Payout', 'الدفعة')} PO-${s.viewing.id}`} hideConfirm
        onClose={() => this.setState({ viewing: null })}>
        <Summary pairs={[
          [this.tx('Provider', 'مقدم الخدمة'), s.viewing.provider], [this.tx('Amount', 'المبلغ'), payoutMoney(s.viewing.amount, s.viewing.currency)],
          [this.tx('Jobs covered', 'الطلبات المشمولة'), '1'], [this.tx('Status', 'الحالة'), <Tag value={s.viewing.status} locale={this.locale()} />],
          [this.tx('Date', 'التاريخ'), payoutDay(s.viewing.date)], [this.tx('Stripe reference', 'مرجع Stripe'), s.viewing.transactionId || this.tx('Cash settlement', 'تسوية نقدية')]
        ]} />
      </Modal>}
      {s.error ? <div className="notice danger"><span className="ni">!</span><span className="nt">{s.error}</span></div>
        : <DataTable columns={this.columns()} rows={s.rows} count={s.count} page={s.page} perPage={s.perPage}
            loading={s.loading} onPage={page => this.setState({ page }, () => this.load())}
            onPerPage={perPage => this.setState({ perPage, page: 1 }, () => this.load())}
            onSearch={search => this.setState({ search, page: 1 }, () => this.load())}
            searchPlaceholder={this.tx('Search payout ID or provider', 'ابحث بمعرّف الدفعة أو مقدم الخدمة')}
            filters={<select className="inp" value={s.payoutStatus} aria-label={this.tx('Filter payout status', 'تصفية حسب حالة الدفعة')}
              onChange={e => this.setState({ payoutStatus: e.target.value, page: 1 }, () => this.load())}>
              <option value="">{this.tx('Status: all', 'الحالة: الكل')}</option><option value="paid">{this.tx('Paid', 'مدفوعة')}</option>
              <option value="pending">{this.tx('Pending', 'معلقة')}</option><option value="failed">{this.tx('Failed', 'فشلت')}</option>
              <option value="held">{this.tx('Held', 'محتجزة')}</option>
            </select>} emptyText={this.tx('No payouts', 'لا توجد دفعات')} emptyHint={this.tx('Completed jobs will appear here.', 'ستظهر دفعات الطلبات المكتملة هنا.')} />}
    </AdminShell>;
  }
}

export default injectIntl(Payouts);
